/* Buy More, Save More product interface. Discount reconciliation lives in bastion-volume-discount-core.js. */

if (!customElements.get('bastion-volume-offer')) {
  customElements.define(
    'bastion-volume-offer',
    class extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.boundForms = new WeakSet();

        this.addEventListener('change', (event) => this.handleChange(event), {
          signal: this.abortController.signal,
        });
        this.addEventListener('click', (event) => this.handleClick(event), {
          signal: this.abortController.signal,
        });
        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) this.closeCheckoutNote();
        }, { signal: this.abortController.signal });
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeCheckoutNote();
        }, { signal: this.abortController.signal });

        this.formObserver?.disconnect();
        const sectionRoot = this.closest('.bastion-essential');

        if (sectionRoot) {
          this.formObserver = new MutationObserver(() => {
            const formId = this.getAttribute('form');
            const nextForm = formId ? document.getElementById(formId) : null;

            if (nextForm && nextForm !== this.form) {
              this.bindForm();
            }
          });
          this.formObserver.observe(sectionRoot, {
            childList: true,
            subtree: true,
          });
        }

        this.bindForm();
        this.querySelectorAll('[data-offer-card]').forEach((card) => {
          card.dataset.quantity = card.dataset.minimum || '1';
        });
        this.update();
        this.lastSelectedRadio = this.querySelector('.bastion-volume-offer__radio:checked') || null;
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.formObserver?.disconnect();
        this.restoreNativeQuantity();
      }

      get selectedCard() {
        const selectedRadio = this.querySelector('.bastion-volume-offer__radio:checked');
        return selectedRadio?.closest('[data-offer-card]') || null;
      }

      get selectedQuantity() {
        const card = this.selectedCard;
        return Math.max(1, Number.parseInt(card?.dataset.quantity || card?.dataset.minimum || '1', 10) || 1);
      }

      get isBundle() {
        return Boolean(this.selectedCard && this.selectedCard.dataset.offer !== 'single');
      }

      bindForm() {
        const formId = this.getAttribute('form');
        const form = formId ? document.getElementById(formId) : null;

        if (!form) {
          return;
        }

        this.form = form;

        if (!this.boundForms.has(form)) {
          this.boundForms.add(form);
          form.addEventListener('variant:change', (event) => this.handleVariantChange(event), {
            signal: this.abortController.signal,
          });
        }

        this.updateNativeControls();
      }

      handleChange(event) {
        if (event.target.matches('.bastion-volume-offer__radio, [data-volume-variant]')) {
          if (event.target.matches('.bastion-volume-offer__radio')) {
            const card = event.target.closest('[data-offer-card]');
            card.dataset.quantity = card.dataset.minimum || '1';
            this.lastSelectedRadio = event.target;
          }

          this.update();
        }
      }

      handleClick(event) {
        const noteToggle = event.target.closest('[data-checkout-note-toggle]');
        const noteClose = event.target.closest('[data-checkout-note-close]');
        const offerButton = event.target.closest('[data-add-volume-offer]');

        if (noteToggle) {
          this.toggleCheckoutNote(noteToggle);
          return;
        }

        if (noteClose) {
          this.closeCheckoutNote();
          return;
        }

        if (offerButton) {
          this.handleOfferSubmit(offerButton);
          return;
        }

        const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');

        if (button) {
          const card = button.closest('[data-offer-card]');
          const minimum = Number.parseInt(card.dataset.minimum || '1', 10);
          const isUnlimited = card.dataset.unlimited === 'true';
          const maximum = isUnlimited ? Number.POSITIVE_INFINITY : Math.max(minimum, Number.parseInt(card.dataset.maximum || minimum, 10));
          const current = Number.parseInt(card.dataset.quantity || minimum, 10);
          const next = button.hasAttribute('data-quantity-plus') ? current + 1 : current - 1;
          card.dataset.quantity = String(Math.min(maximum, Math.max(minimum, next)));
          this.update();
          return;
        }

        const tierLabel = event.target.closest('.bastion-volume-offer__tier-label');
        if (!tierLabel) return;

        const clickedCard = tierLabel.closest('[data-offer-card]');
        const clickedRadio = clickedCard?.querySelector('.bastion-volume-offer__radio');

        if (clickedRadio && clickedRadio === this.lastSelectedRadio && clickedRadio.checked) {
          if (!event.target.matches('.bastion-volume-offer__radio')) event.preventDefault();
          clickedRadio.checked = false;
          this.lastSelectedRadio = null;
          this.update();
        }
      }

      toggleCheckoutNote(button) {
        const popover = this.querySelector('[data-checkout-note-popover]');
        if (!popover) return;

        const willOpen = popover.hidden;
        popover.hidden = !willOpen;
        button.setAttribute('aria-expanded', String(willOpen));
      }

      closeCheckoutNote() {
        const popover = this.querySelector('[data-checkout-note-popover]');
        const button = this.querySelector('[data-checkout-note-toggle]');
        if (popover) popover.hidden = true;
        if (button) button.setAttribute('aria-expanded', 'false');
      }

      handleVariantChange(event) {
        if (event.detail?.formId !== this.getAttribute('form') || !event.detail.variant?.id) {
          return;
        }

        const previousId = String(event.detail.previousVariant?.id || '');
        const nextId = String(event.detail.variant.id);

        this.querySelectorAll('[data-volume-variant]').forEach((select) => {
          if (!previousId || select.value === previousId) {
            const nextOption = Array.from(select.options).find((option) => option.value === nextId && !option.disabled);

            if (nextOption) {
              select.value = nextId;
            }
          }
        });

        this.dataset.currentVariantId = nextId;
        this.update();
      }

      ensureVariantRows(quantity) {
        const list = this.querySelector('.bastion-volume-offer__variant-list');
        const template = this.querySelector('[data-volume-unit-template]');

        if (!list || !template) return;

        let rowCount = list.querySelectorAll('[data-volume-unit]').length;

        while (rowCount < quantity) {
          const unitNumber = rowCount + 1;
          const fragment = template.content.cloneNode(true);
          const row = fragment.querySelector('[data-volume-unit]');
          const label = fragment.querySelector('[data-volume-unit-label]');
          const select = fragment.querySelector('[data-volume-variant]');
          const selectId = `${this.id}-variant-${unitNumber}`;

          row.dataset.volumeUnit = String(unitNumber);
          label.htmlFor = selectId;
          label.textContent = `${this.dataset.unitLabel || 'Item'} ${unitNumber}`;
          select.id = selectId;

          const currentOption = Array.from(select.options).find(
            (option) => option.value === String(this.dataset.currentVariantId || '') && !option.disabled
          );

          if (currentOption) select.value = currentOption.value;

          list.append(fragment);
          rowCount += 1;
        }
      }

      update() {
        const selectedCard = this.selectedCard;

        if (!selectedCard) {
          this.querySelectorAll('[data-offer-card]').forEach((card) => {
            card.removeAttribute('data-selected');
            const quantityControl = card.querySelector('[data-tier-quantity]');
            if (quantityControl) quantityControl.hidden = true;
          });

          const variantsPanel = this.querySelector('[data-volume-variants]');
          if (variantsPanel) variantsPanel.hidden = true;

          this.querySelectorAll('[data-volume-unit]').forEach((row) => {
            row.hidden = true;
            row.querySelector('[data-volume-variant]')?.setAttribute('disabled', '');
          });

          this.dataset.mode = 'none';
          this.updateTierMath();
          this.updateNativeControls();

          const offerButton = this.querySelector('[data-add-volume-offer]');
          if (offerButton) offerButton.hidden = true;

          const status = this.querySelector('[data-volume-status]');
          if (status && !status.hasAttribute('data-error')) status.textContent = '';
          return;
        }

        this.querySelectorAll('[data-offer-card]').forEach((card) => {
          const isSelected = card === selectedCard;
          card.toggleAttribute('data-selected', isSelected);
          const quantityControl = card.querySelector('[data-tier-quantity]');

          if (quantityControl) {
            quantityControl.hidden = !isSelected;
            const output = quantityControl.querySelector('[data-quantity-output]');

            if (output) {
              output.textContent = card.dataset.quantity || card.dataset.minimum || '1';
            }

            const minimum = Number.parseInt(card.dataset.minimum || '1', 10);
            const isUnlimited = card.dataset.unlimited === 'true';
            const maximum = isUnlimited ? Number.POSITIVE_INFINITY : Math.max(minimum, Number.parseInt(card.dataset.maximum || minimum, 10));
            const quantity = Number.parseInt(card.dataset.quantity || minimum, 10);
            const minus = quantityControl.querySelector('[data-quantity-minus]');
            const plus = quantityControl.querySelector('[data-quantity-plus]');

            if (minus) minus.disabled = quantity <= minimum;
            if (plus) plus.disabled = !isUnlimited && quantity >= maximum;
          }
        });

        const variantsPanel = this.querySelector('[data-volume-variants]');
        const selectableVariantCount = Array.from(
          this.querySelector('[data-volume-variant]')?.options || []
        ).filter((option) => !option.disabled).length;

        if (variantsPanel) {
          if (this.isBundle && variantsPanel.parentElement !== selectedCard) {
            selectedCard.append(variantsPanel);
          }
          variantsPanel.hidden = !this.isBundle || selectableVariantCount <= 1;
        }

        if (this.isBundle) {
          this.ensureVariantRows(this.selectedQuantity);
        }

        this.querySelectorAll('[data-volume-unit]').forEach((row, index) => {
          row.hidden = !this.isBundle || index >= this.selectedQuantity;
          row.querySelector('[data-volume-variant]')?.toggleAttribute('disabled', row.hidden);
        });

        this.dataset.mode = this.isBundle ? 'bundle' : 'single';
        this.updateTierMath();
        this.updateNativeControls();
        const offerButton = this.querySelector('[data-add-volume-offer]');
        if (offerButton) offerButton.hidden = !this.isBundle;
        const status = this.querySelector('[data-volume-status]');
        if (status && !status.hasAttribute('data-error')) status.textContent = '';
      }

      updateTierMath() {
        const unitSelects = Array.from(this.querySelectorAll('[data-volume-variant]'));
        const fallbackPrice = Number.parseInt(
          unitSelects[0]?.selectedOptions[0]?.dataset.price || unitSelects[0]?.options[0]?.dataset.price || '0',
          10
        );

        this.querySelectorAll('[data-offer-card]').forEach((card) => {
          const quantity = card.hasAttribute('data-selected')
            ? Math.max(1, Number.parseInt(card.dataset.quantity || card.dataset.minimum || '1', 10))
            : Math.max(1, Number.parseInt(card.dataset.minimum || '1', 10));
          const discount = Math.min(100, Math.max(0, Number.parseFloat(card.dataset.discount || '0')));
          let originalTotal = 0;

          for (let index = 0; index < quantity; index += 1) {
            const optionPrice = Number.parseInt(unitSelects[index]?.selectedOptions[0]?.dataset.price || fallbackPrice || '0', 10);
            originalTotal += optionPrice;
          }

          const discountedTotal = Math.round(originalTotal * (100 - discount) / 100);
          const math = card.querySelector('[data-tier-math]');

          if (!math) return;

          const original = math.querySelector('[data-tier-original]');
          const total = math.querySelector('[data-tier-total]');
          const savings = math.querySelector('[data-tier-savings]');

          if (!total) return;

          if (discount > 0) {
            if (original) {
              original.hidden = false;
              original.textContent = this.formatMoney(originalTotal);
            }
            total.textContent = this.formatMoney(discountedTotal);
            if (savings) {
              savings.hidden = false;
              savings.textContent = `${this.dataset.savingsLabel || 'You save'} ${this.formatMoney(originalTotal - discountedTotal)}`;
            }
          } else {
            if (original) {
              original.hidden = true;
              original.textContent = '';
            }
            total.textContent = this.formatMoney(originalTotal);
            if (savings) {
              savings.hidden = true;
              savings.textContent = '';
            }
          }
        });
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.dataset.currencyCode || 'USD',
            maximumFractionDigits: 2,
          }).format((Number(cents) || 0) / 100);
        } catch (error) {
          return `${((Number(cents) || 0) / 100).toFixed(2)}`;
        }
      }

      updateNativeControls() {
        if (!this.form) {
          return;
        }

        const quantitySelector = this.form.querySelector('quantity-selector');
        const quantityInput = quantitySelector?.querySelector('[name="quantity"]');
        const buttonContent = this.form.querySelector('button[type="submit"] button-content');

        if (quantitySelector) {
          if (!quantitySelector.hasAttribute('data-bastion-volume-original-hidden')) {
            quantitySelector.dataset.bastionVolumeOriginalHidden = quantitySelector.hidden ? 'true' : 'false';
          }

          quantitySelector.hidden = quantitySelector.dataset.bastionVolumeOriginalHidden === 'true';
        }

        if (quantityInput) {
          if (!quantityInput.hasAttribute('data-bastion-volume-original-disabled')) {
            quantityInput.dataset.bastionVolumeOriginalDisabled = quantityInput.disabled ? 'true' : 'false';
          }

          quantityInput.disabled = quantityInput.dataset.bastionVolumeOriginalDisabled === 'true';
        }

        if (buttonContent) {
          if (!buttonContent.dataset.bastionVolumeOriginalLabel) {
            buttonContent.dataset.bastionVolumeOriginalLabel = buttonContent.textContent.trim();
          }

          const nextLabel = buttonContent.dataset.bastionVolumeOriginalLabel;

          if (buttonContent.textContent !== nextLabel) {
            buttonContent.textContent = nextLabel;
          }
        }
      }

      restoreNativeQuantity() {
        if (!this.form) return;

        const quantitySelector = this.form.querySelector('quantity-selector');
        const quantityInput = quantitySelector?.querySelector('[name="quantity"]');

        if (quantitySelector?.dataset.bastionVolumeOriginalHidden) {
          quantitySelector.hidden = quantitySelector.dataset.bastionVolumeOriginalHidden === 'true';
        }

        if (quantityInput?.dataset.bastionVolumeOriginalDisabled) {
          quantityInput.disabled = quantityInput.dataset.bastionVolumeOriginalDisabled === 'true';
        }
      }

      getLineItemProperties() {
        const properties = {};

        this.form.querySelectorAll('[name^="properties["]').forEach((field) => {
          if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
          if (field.value === '') return;

          const match = field.name.match(/^properties\[(.+)]$/);
          if (match) properties[match[1]] = field.value;
        });

        return properties;
      }

      buildItems() {
        const properties = this.getLineItemProperties();
        const sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value;
        const items = Array.from(this.querySelectorAll('[data-volume-unit]:not([hidden]) [data-volume-variant]')).map((select) => {
          const item = { id: select.value, quantity: 1 };
          if (Object.keys(properties).length) item.properties = properties;
          if (sellingPlan) item.selling_plan = sellingPlan;
          return item;
        });

        document.querySelectorAll(`bastion-product-add-ons[form="${CSS.escape(this.form.id)}"]`).forEach((addOns) => {
          const addOnQuantity = addOns.hasAttribute('match-main-quantity') ? this.selectedQuantity : 1;

          addOns.querySelectorAll('[data-add-on-checkbox]:checked:not(:disabled)').forEach((checkbox) => {
            items.push({ id: checkbox.value, quantity: addOnQuantity });
          });
        });

        return this.mergeItems(items);
      }

      mergeItems(items) {
        const merged = new Map();

        items.forEach((item) => {
          const key = JSON.stringify({ id: item.id, selling_plan: item.selling_plan || null, properties: item.properties || {} });

          if (merged.has(key)) {
            merged.get(key).quantity += item.quantity;
          } else {
            merged.set(key, { ...item, properties: item.properties ? { ...item.properties } : undefined });
          }
        });

        return Array.from(merged.values());
      }

      async handleOfferSubmit(offerButton) {
        if (!this.isBundle) {
          return;
        }

        if (offerButton.getAttribute('aria-busy') === 'true') {
          return;
        }

        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        const items = this.buildItems();
        if (items.length === 0) {
          this.showError('Choose an available variant for every pen.');
          return;
        }

        offerButton.setAttribute('aria-busy', 'true');

        const sectionsToBundle = new Set();
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }));
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items, sections: Array.from(sectionsToBundle).join(',') }),
          });
          const responseJson = await response.json();

          if (!response.ok) {
            throw new Error(responseJson.description || responseJson.message || 'The offer could not be added to the cart.');
          }

          let cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((cartResponse) => cartResponse.json());
          const discountResult = await bastionApplyTierDiscount(
            this,
            '',
            Array.from(sectionsToBundle),
            cart
          );
          cart = discountResult.cart || cart;
          const discountError = discountResult.error;

          cart.sections ||= responseJson.sections;

          if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
            window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
            return;
          }

          const addedItems = responseJson.items || [];

          this.form.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: addedItems, cart },
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart },
          }));
          if (discountError) this.showError(discountError);
        } catch (error) {
          this.form.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: error.message },
          }));
          this.showError(error.message);
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } finally {
          offerButton.removeAttribute('aria-busy');
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }

      showError(message) {
        const status = this.querySelector('[data-volume-status]');
        if (status) {
          status.dataset.error = '';
          status.textContent = message;
        }
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message, tone: 'error' },
        }));
      }
    }
  );
}
